import { useMemo, useState } from 'react'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
} from '@mui/material'
import { navItems } from '../../data/landing'
import logo from '../../assets/images/logo.jpeg'

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false)

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev)
  }

  const handleNavClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    if (!href.startsWith('#')) return

    event.preventDefault()

    const target = document.querySelector(href)
    if (!target) return

    target.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })

    setMobileOpen(false)
  }

  const drawerContent = useMemo(
    () => (
      <Box
        sx={{
          height: '100%',
          background:
            'linear-gradient(180deg, rgba(10,10,10,0.98), rgba(18,18,18,0.98))',
          color: '#fff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 2,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              height: 48,
              width: 'auto',
              objectFit: 'contain',
            }}
          />

          <IconButton onClick={handleDrawerToggle} sx={{ color: '#fff' }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <List sx={{ px: 1.5, py: 2 }}>
          {navItems.map((item) => (
            <ListItemButton
              key={item.label}
              component="a"
              href={item.href}
              onClick={(event) => handleNavClick(event, item.href)}
              sx={{
                borderRadius: 3,
                mb: 0.5,
                py: 1.2,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.06)',
                },
              }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#fff',
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>
    ),
    []
  )

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: 'rgba(10,10,10,0.72)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 72, md: 80 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <Box
                component="a"
                href="#top"
                onClick={(event) => handleNavClick(event, '#top')}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  component="img"
                  src={logo}
                  alt="Logo"
                  sx={{
                    height: { xs: 48, md: 56 },
                    width: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1,
              }}
            >
              {navItems.map((item) => (
                <Button
                  key={item.label}
                  component="a"
                  href={item.href}
                  onClick={(event) => handleNavClick(event, item.href)}
                  sx={{
                    color: '#fff',
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 1.75,
                    py: 1,
                    borderRadius: 999,
                    transition:
                      'background-color 0.2s ease, color 0.2s ease, transform 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>

            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                color: '#fff',
                width: 44,
                height: 44,
                border: '1px solid rgba(255,255,255,0.1)',
                backgroundColor: 'rgba(255,255,255,0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.08)',
                },
              }}
              aria-label="Open navigation menu"
            >
              <MenuRoundedIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            width: 280,
            background: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  )
}